import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LabelList,
} from 'recharts';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import { DIRETORIA_PIE_COLORS } from '@/components/Diretoria/diretoriaColors';
import { formatCurrency, formatCurrencyCompact } from '@/lib/formatters';

export interface DiretoriaModalChartProps {
  indicator: DiretoriaIndicator;
  height?: number;
  /** Pizza: sem rótulos no arco; legenda abaixo (recomendado para muitas fatias). */
  pieUseLegend?: boolean;
}

export const DiretoriaModalChart: React.FC<DiretoriaModalChartProps> = ({
  indicator,
  height = 260,
  pieUseLegend,
}) => {
  const h = height;
  const useLegend =
    pieUseLegend ?? (indicator.chartKind === 'pie' && (indicator.pieData?.length ?? 0) >= 5);

  if (indicator.chartKind === 'none') {
    return (
      <div
        className="flex w-full min-w-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/30 text-sm text-muted-foreground"
        style={{ height: h }}
      >
        Gráfico indisponível neste indicador — ver tabela e notas abaixo.
      </div>
    );
  }

  if (indicator.chartKind === 'pie' && indicator.pieData?.length) {
    return (
      <div style={{ height: h }} className="w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart margin={{ top: 8, right: 8, left: 8, bottom: useLegend ? 8 : 8 }}>
            <Pie
              data={indicator.pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="42%"
              outerRadius={useLegend ? 72 : 88}
              innerRadius={0}
              label={useLegend ? false : ({ name, percent }) => `${name} ${(((percent ?? 0) as number) * 100).toFixed(0)}%`}
            >
              {indicator.pieData.map((_, i) => (
                <Cell key={i} fill={DIRETORIA_PIE_COLORS[i % DIRETORIA_PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(v: number) => [v, 'Qtd.']} />
            {useLegend ? (
              <Legend
                layout="horizontal"
                verticalAlign="bottom"
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              />
            ) : null}
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const data = indicator.series ?? [];
  const key = indicator.barSeriesKey ?? 'valor';
  const formatAsCurrency = indicator.id === 'custo-moto';
  if (indicator.chartKind === 'bar') {
    return (
      <div style={{ height: h }} className="w-full min-h-0 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: formatAsCurrency ? 4 : 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={data.length > 6 ? -25 : 0} textAnchor={data.length > 6 ? 'end' : 'middle'} height={data.length > 6 ? 56 : 30} />
            <YAxis
              tick={{ fontSize: 10 }}
              width={formatAsCurrency ? 52 : 36}
              tickFormatter={formatAsCurrency ? (v) => formatCurrencyCompact(Number(v)) : undefined}
            />
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload as { name: string; valor: number; valor2?: number };
                return (
                  <div className="rounded-md border border-border bg-background px-2 py-1.5 text-xs shadow-md">
                    <div className="font-medium text-foreground">{row.name}</div>
                    <div className="text-muted-foreground">
                      {formatAsCurrency ? 'Custo médio' : 'Valor'}:{' '}
                      {formatAsCurrency ? formatCurrency(Number(row.valor)) : row.valor}
                    </div>
                    {row.valor2 != null ? (
                      <div className="text-muted-foreground">Qtd.: {row.valor2}</div>
                    ) : null}
                  </div>
                );
              }}
            />
            <Bar dataKey={key} fill="hsl(222 47% 18%)" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div style={{ height: h }} className="w-full min-h-0 min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} width={36} />
          <Tooltip />
          <Line type="monotone" dataKey="valor" stroke="hsl(222 47% 18%)" strokeWidth={2} dot={false} name="Série A" />
          {data.some((d) => d.valor2 != null) ? (
            <Line type="monotone" dataKey="valor2" stroke="#ca8a04" strokeWidth={2} dot={false} name="Série B" />
          ) : null}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export interface DiretoriaOverviewEmpresaPoint {
  name: string;
  valor: number;
  valor2?: number;
}

/** Barras horizontais: comprimento = valor em R$ (`valor2`); `valor` = quantidade de títulos (tooltip). */
export const DiretoriaEmpresasBarChart: React.FC<{
  data: DiretoriaOverviewEmpresaPoint[];
  height?: number;
}> = ({ data, height = 280 }) => {
  const useMoney = data.some((d) => (d.valor2 ?? 0) > 0);
  const dataKey = useMoney ? 'valor2' : 'valor';

  return (
    <div style={{ height }} className="w-full min-h-0 min-w-0">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart layout="vertical" data={data} margin={{ top: 8, right: 100, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 10 }}
            tickFormatter={(v) => formatCurrencyCompact(Number(v))}
          />
          <YAxis type="category" dataKey="name" width={108} tick={{ fontSize: 10 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload as DiretoriaOverviewEmpresaPoint;
              return (
                <div className="rounded-md border border-border bg-background px-2 py-1.5 text-xs shadow-md">
                  <div className="font-medium text-foreground">{row.name}</div>
                  {useMoney ? (
                    <>
                      <div className="text-muted-foreground">Saldo: {formatCurrency(Number(row.valor2 ?? 0))}</div>
                      <div className="text-muted-foreground">Títulos: {row.valor}</div>
                    </>
                  ) : (
                    <div className="text-muted-foreground">Títulos: {row.valor}</div>
                  )}
                </div>
              );
            }}
          />
          <Bar dataKey={dataKey} fill="hsl(222 47% 18%)" name="Saldo (R$)" radius={[0, 4, 4, 0]}>
            <LabelList
              dataKey={dataKey}
              position="right"
              content={({ x, y, width, height, value }) => {
                const val = Number(value);
                if (Number.isNaN(val)) return null;
                const tx = Number(x) + Number(width) + 6;
                const ty = Number(y) + Number(height) / 2;
                return (
                  <text
                    x={tx}
                    y={ty}
                    dominantBaseline="middle"
                    fill="hsl(222 47% 18%)"
                    fontSize={10}
                    fontWeight={600}
                  >
                    {formatCurrency(val)}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
